import { Link } from "@tanstack/react-router";
import { useBillingStateQuery } from "@/frontend/queries/dashboard/billing";
import { PLAN_TIER_LABEL } from "@/frontend/constants/plans";
import { useSidebar } from "@/frontend/components/ui/sidebar";
import { useWorkspacePersona } from "./individual/useWorkspacePersona";
import { cn } from "@/frontend/lib/utils";

/**
 * Sidebar footer card showing the workspace's patient usage against its plan
 * cap. Hidden when the sidebar is collapsed to icons, on plans without a cap,
 * for orgs with no `billing_state` row yet, and in an individual workspace,
 * where one person counting themselves against a patient cap means nothing.
 */
export function WorkspaceQuotaCard() {
  const { state: sidebarState, isMobile } = useSidebar();
  const { isIndividual } = useWorkspacePersona();
  const { data: billing } = useBillingStateQuery();

  if (isIndividual) return null;
  if (sidebarState === "collapsed" && !isMobile) return null;
  if (!billing) return null;
  if (billing.active_patient_limit <= 0) return null;

  const used = billing.active_patient_count;
  const limit = billing.active_patient_limit;
  const ratio = Math.min(1, used / limit);
  const atCap = used >= limit;
  const nearCap = ratio >= 0.8;

  return (
    <Link
      to="/dashboard/settings"
      search={{ section: "usage", destinationId: undefined, provider: "", webhookId: undefined }}
      className="mx-2 block rounded-lg border bg-background px-3 py-2.5 transition-colors hover:bg-accent/40"
    >
      <div className="text-xs font-medium">
        {PLAN_TIER_LABEL[billing.plan_code] ?? billing.plan_code}
      </div>
      <div className="text-[10px] text-muted-foreground tabular-nums">
        {used.toLocaleString()} of {limit.toLocaleString()} patients
      </div>
      <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            atCap ? "bg-destructive" : nearCap ? "bg-amber-500" : "bg-primary",
          )}
          style={{ width: `${Math.max(ratio * 100, used > 0 ? 2 : 0)}%` }}
        />
      </div>
    </Link>
  );
}
